import React, { useState, useEffect, useRef, useCallback } from "react";
import { Button, Label, Popup } from "semantic-ui-react";
import { toast } from "react-toastify";

import { ITableAppRender } from "../../components/TableApp";
import TemplateTable from "../../components/TemplateTable";
import CadastroPessoa from "../../components/CadastroPessoa";

import SectorManagerRepository, { ISectorManagerListModel } from "../../Repositories/SectorManagerRepository";
import { ISearch } from "../../components/Search";
import { ISearchResponse } from "../../interfaces/Search"
import EditarPessoa from "../../components/EditarPessoa";

import errors from "../../Utils/ExtractErrors";
import { GetUser } from "../../services/auth";
import { IFields } from "../../components/Search/FilterBuilder";
import ModalYesNo from "../../components/ModalYesNo";
import UsuarioRepository from "../../Repositories/UsuarioRepository";
import { eStatus } from "../../interfaces";
import { format } from "date-fns";

const SectorManagers: React.FC = () => {
    const [list, setList] = useState<ISectorManagerListModel[]>([]);
    const [loading, setLoading] = useState<boolean>(false);

    const refSearch = useRef<ISearch>({ searchString: "", type: "simples" });
    const refRecordsByPage = useRef<number>(20);
    const refPageNumber = useRef<string | number | undefined>(1);
    const refTotalRecords = useRef<number>(0);
    const refTotalPages = useRef<number>(1);

    const { role, refId } = GetUser();

    const onChangeRecords = (value: number) => {
        refRecordsByPage.current = value;
        load();
    }

    const onPageChange = (pageNumber: string | number | undefined) => {
        refPageNumber.current = pageNumber;
        load();
    }

    const load = useCallback(async () => {

        setLoading(true);
        try {
            let response: ISearchResponse<ISectorManagerListModel>;

            if (refSearch.current.type === "simples") {
                response = await SectorManagerRepository.List({
                    search: refSearch.current.searchString,
                    pageNumber: refPageNumber.current,
                    recordsByPage: refRecordsByPage.current
                });
            } else {
                response = await SectorManagerRepository.List({
                    filter: refSearch.current.searchString,
                    pageNumber: refPageNumber.current,
                    recordsByPage: refRecordsByPage.current
                });
            }

            refTotalRecords.current = response.totalRecords;
            refTotalPages.current = response.totalPages;

            setList(response.list);
        } catch (err) {
            if (err.response.status === 401) {
                window.location.href = "/";
            }

            const error = errors.getAllErrors(err.response.data);

            if (error !== undefined)
                toast(error, { position: "top-center", type: "error" });
        }
        setLoading(false);
    }, []);

    const onSearch = ({ searchString, type }: ISearch) => {
        refSearch.current.searchString = searchString;
        refSearch.current.type = type;

        load();
    }

    useEffect(() => { load() }, [load]);

    const setStatus = (devedor: number, status: eStatus, data: string) => {
        if (devedor === 2) {
            return <Popup
                trigger={<Label ribbon icon="thumbs up" color="orange" content="NEG" />}
                content={`Data de cadastro: ${format(new Date(data), "dd/MM/yyyy")}`}
                wide
            />
        }
        if (devedor === 1) {
            return <Popup
                trigger={<Label ribbon icon="user times" color="red" content="DEV" />}
                content={`Data de cadastro: ${format(new Date(data), "dd/MM/yyyy")}`}
                wide
            />
        }
        else {
            switch (status) {
                case eStatus.Inativo:
                    return <Popup
                        trigger={<Label ribbon icon="times" color="grey" content="INA" />}
                        content={`Data de cadastro: ${format(new Date(data), "dd/MM/yyyy")}`}
                        wide
                    />
                case eStatus.Ativo:
                    return <Popup
                        trigger={<Label ribbon icon="check" color="pink" content="ATI" />}
                        content={`Data de cadastro: ${format(new Date(data), "dd/MM/yyyy")}`}
                        wide
                    />
                case eStatus.Pendente:
                    return <Popup
                        trigger={<Label ribbon icon="clock" color="orange" content="PEN" />}
                        content={`Data de cadastro: ${format(new Date(data), "dd/MM/yyyy")}`}
                        wide
                    />
                default:
                    return "";
            }

        }
    }

    const tableRender: ITableAppRender[] = [
        {
            header: "Status",
            content: (a: ISectorManagerListModel) => setStatus(a.devedor, a.status, a.data),
            textAlign: "left",
            width: 1
        },
        {
            header: "CPF",
            content: (a: ISectorManagerListModel) => a.cpf,
            width: 2
        },
        {
            header: "Nome",
            content: (a: ISectorManagerListModel) => a.nome,
            width: 3
        },
        {
            header: "G. Regional",
            content: (a: ISectorManagerListModel) => a.gerenteRegional,
            width: 3
        },
        {
            header: "Cidade",
            content: (a: ISectorManagerListModel) => a.cidade,
            width: 2
        },
    ];

    const fields: IFields[] = [
        { label: "G. Regional", name: "GerenteRegional.Nome", type: "string", roles: ["Admin", "Operador"] },
        { label: "G. Setor", name: "Nome", type: "string", roles: ["Admin", "Operador", "GerenteR"] },
        { label: "Cidade", name: "Cidade", type: "string", roles: ["Admin", "Operador", "GerenteR", "GerenteS"] },
        {
            label: "Status",
            name: "Status",
            type: "enum",
            values: [
                { key: "INA", text: "INA", value: 0, label: { icon: "times", color: "grey", content: "INA" } },
                { key: "ATI", text: "ATI", value: 1, label: { icon: "check", color: "pink", content: "ATI" } },
                { key: "PEN", text: "PEN", value: 2, label: { icon: "clock", color: "orange", content: "PEN" } }
            ],
            roles: ["Admin", "Operador", "GerenteR"]
        },
        {
            label: "Devedor",
            name: "Devedor",
            type: "enum",
            values: [
                { key: "NÃO", text: "NÃO", value: 0, label: { icon: "user", color: "green", content: "NDV" } },
                { key: "SIM", text: "SIM", value: 1, label: { icon: "user times", color: "red", content: "DEV" } },
            ],
            roles: ["Admin", "Operador", "GerenteR"]
        }
    ];

    const onReset = async (id: string) => {
        try {
            const response = await UsuarioRepository.Reset({ id });

            toast(`🔑 ${response.message}`, { position: "top-center", type: "success" });
            load();
        } catch (error) {
            return toast("❌ Erro ao resetar a senha.", { position: "top-center", type: "error" });
        }
    }

    return (
        <TemplateTable
            title="Gerentes de setor"
            icon="user"
            insert={role === "GerenteR" ? <CadastroPessoa url="/gerentesetor" grId={refId} status="2" onSuccess={load} /> : undefined}

            searchPlaceHolder={"Nome"}
            onSearch={onSearch}
            filterFields={fields}

            tableLoading={loading}
            tableRender={tableRender}
            tableList={list}
            tableActions={(data: ISectorManagerListModel, index: number) => [
                <EditarPessoa id={data.id || ""} url={`/gerentesetor/${data.id}`} onSuccess={load} />,
                <ModalYesNo
                    trigger={
                        role === "Admin" &&
                        <Button
                            icon="refresh"
                            color="green"
                        />
                    }
                    header="Resetar senha"
                    content={`Deseja realmente resetar a senha do usuario ${data.nome}`}
                    onSuccess={() => onReset(data.id || "")}
                />
            ]}
            tablePagination={{
                currentPage: refPageNumber.current,
                totalRecords: refTotalRecords.current,
                totalPages: refTotalPages.current,
                onPageChange: onPageChange
            }}
            tableOnChangeRecords={onChangeRecords}
        />
    );
};

export default SectorManagers;
